import { useEffect, useRef } from 'react';
import { Subscription } from '@rails/actioncable';
import { useActionCable } from '../providers/ActionCableProvider';

export interface MessageData {
  type: 'received_message';
  conversation: any;
  message: any;
}

/**
 * Hook to subscribe to new message notifications via Action Cable.
 * Replaces Pusher channel: message-listner-{userId}
 *
 * @param userId - The user ID to subscribe for
 * @param onMessage - Callback when a new message is received
 */
export const useMessageChannel = (
  userId: string | null | undefined,
  onMessage: (data: MessageData) => void,
) => {
  const { subscribe, unsubscribe, connected } = useActionCable();
  const subscriptionRef = useRef<Subscription | null>(null);
  const onMessageRef = useRef(onMessage);

  // Keep callback ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!userId || !connected) return;

    subscriptionRef.current = subscribe(
      'MessageChannel',
      {},
      {
        received: (data: MessageData) => {
          if (data.type === 'received_message') {
            onMessageRef.current(data);
          }
        },
      },
    );

    return () => {
      if (subscriptionRef.current) {
        unsubscribe(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [userId, connected, subscribe, unsubscribe]);
};

export default useMessageChannel;
